import { AccountSide, GameBetKey, GameType } from '@hit-game/shared/enums';
import type { IGroupMember, SideConfig } from '@hit-game/shared/types';
import {
  REQUIRED_GROUP_MEMBERS,
  doorExposureWarning,
  groupIssues,
  normalizeSideConfigs,
  sideImbalanceWarning,
  unsplittableSideWarning,
} from '@hit-game/shared/utils';

/** Thành viên ở dạng đang soạn — giờ chỉ còn tài khoản, không còn phe hay cửa. */
export interface DraftMember {
  accountId: string;
}

export function toDraft(members: IGroupMember[]): DraftMember[] {
  return members.map((member) => ({ accountId: String(member.accountId) }));
}

export function toMembers(drafts: DraftMember[]): IGroupMember[] {
  return drafts.map((draft) => ({ accountId: draft.accountId }));
}

/** Lưới tiền đang soạn: phe → cửa → số tiền. Cửa vắng mặt = 0. */
export type DoorGrid = Record<AccountSide, Partial<Record<GameBetKey, number>>>;

/** Lưới trống — dùng khi đổi game: cửa gắn với game nên tiền cũ không còn nghĩa. */
export function emptyGrid(): DoorGrid {
  return { [AccountSide.CHAN]: {}, [AccountSide.LE]: {} };
}

/** `sideConfigs` của nhóm (có thể còn dạng cũ) → lưới theo cửa của `gameType`. */
export function toGrid(sideConfigs: SideConfig[] | undefined, gameType: GameType): DoorGrid {
  const normalized = normalizeSideConfigs(sideConfigs, gameType);
  const grid = emptyGrid();
  for (const config of normalized) {
    for (const door of config.doors) grid[config.side][door.key] = door.amount;
  }
  return grid;
}

/**
 * Lưới trên form → `sideConfigs` mà API nhận: luôn đủ 2 phe × MỌI cửa của game, đúng
 * thứ tự. Đi qua `normalizeSideConfigs` nên cửa lạ (của game khác) tự rơi mất.
 */
export function toSideConfigs(grid: DoorGrid, gameType: GameType): SideConfig[] {
  return normalizeSideConfigs(
    Object.values(AccountSide).map((side) => ({
      side,
      doors: Object.entries(grid[side]).map(([key, amount]) => ({ key, amount })),
    })),
    gameType,
  );
}

/** Tiền một ô của lưới (0 nếu chưa nhập). */
export function gridAmount(grid: DoorGrid, side: AccountSide, key: GameBetKey): number {
  return grid[side][key] ?? 0;
}

/**
 * Lỗi CHẶN lưu — cùng bộ luật với server, báo ngay tại chỗ thay vì đợi một vòng
 * request. Chỉ giữ lại thứ server thật sự từ chối; "chưa đủ 4 tài khoản" hay
 * "chưa đặt tiền" là nhóm chưa CHẠY được chứ không phải dữ liệu sai, nên nằm ở
 * `warnDrafts` để người dùng vẫn lưu được nửa chừng.
 */
export function validateDrafts(drafts: DraftMember[]): string | null {
  if (drafts.some((draft) => !draft.accountId)) return 'Còn dòng chưa chọn tài khoản';

  const ids = drafts.map((draft) => draft.accountId);
  if (new Set(ids).size !== ids.length) {
    return 'Một tài khoản chỉ được xuất hiện 1 lần trong nhóm';
  }

  if (drafts.length > REQUIRED_GROUP_MEMBERS) {
    return `Nhóm chỉ nhận tối đa ${String(REQUIRED_GROUP_MEMBERS)} tài khoản`;
  }

  return null;
}

/**
 * Cảnh báo hiện dưới form — không chặn nút Lưu. Rỗng = không có gì để nói.
 *
 * Đọc ĐÚNG các hàm mà server dùng để từ chối chạy nhóm và để log cảnh báo lúc Start,
 * nên chữ hiện ở đây khớp từng ký tự với phía server. Chép đôi luật là form mời một
 * đằng, server chặn một nẻo.
 *
 * Nhóm CHƯA chạy được thì chỉ nói lý do chưa chạy được; có chạy được rồi mới tới ba
 * cảnh báo "chạy được nhưng đáng ngờ" (lệch phe, cược trần, phe không chia nổi) — nói
 * cả hai loại cùng lúc là người dùng không biết cái nào phải sửa trước.
 */
export function warnDrafts(
  drafts: DraftMember[],
  gameType: GameType,
  sideConfigs: SideConfig[],
): string[] {
  if (drafts.length === 0) return [];

  const shape = { gameType, memberCount: drafts.length, sideConfigs };
  const issues = groupIssues(shape);
  if (issues.length > 0) return [`Chưa chạy được — ${issues.join('; ')}.`];

  return [
    sideImbalanceWarning(shape),
    doorExposureWarning(shape),
    ...unsplittableSideWarning(shape),
  ].filter((item): item is string => item !== null);
}

/**
 * Chữ ký của một nhóm để so "đã sửa gì chưa".
 *
 * Đi qua `toMembers`/`normalizeSideConfigs` cho CẢ hai vế: dữ liệu dưới DB giữ nguyên
 * thứ tự lúc ghi, còn bản nháp thì theo thứ tự trên form — so thẳng hai mảng là
 * mở thẻ lên chưa gõ gì đã thấy nút Lưu sáng.
 */
export function draftSignature(input: {
  name: string;
  gameType: GameType;
  sideConfigs: SideConfig[] | undefined;
  members: DraftMember[];
}): string {
  return JSON.stringify({
    name: input.name.trim(),
    gameType: input.gameType,
    // Qua `normalizeSideConfigs` cho cả hai vế: nhóm đọc từ DB có thể còn dạng cũ hoặc
    // khác thứ tự, còn bản nháp thì luôn đủ cửa.
    sideConfigs: normalizeSideConfigs(input.sideConfigs, input.gameType),
    members: toMembers(input.members),
  });
}
